import React, { useEffect, useRef, useState } from 'react';
import instantsearch from 'instantsearch.js';
import { searchBox, hits, pagination } from 'instantsearch.js/es/widgets';
import moment from 'moment';
import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import styles from './BrowseTest.module.scss';
import config from '../../config';
import { Search } from 'lucide-react';
import FilterBar from '../../components/BrowseTest/FilterBar';
import FilterModal from '../../components/BrowseTest/FilterModal/FilterModal';
import Loader from 'components/Loader/Loader';

const BrowseTest = () => {
  const searchContainerRef = useRef(null);
  const hitsContainerRef = useRef(null);
  const paginationContainerRef = useRef(null);

  // Keep search instance in state so child components re-render when it's ready
  const [searchInstance, setSearchInstance] = useState(null);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [activeFilterCount, setActiveFilterCount] = useState(0);

  useEffect(() => {
    const typesenseInstantsearchAdapter = new TypesenseInstantSearchAdapter({
      server: {
        apiKey: config.typesense.apiKeySearchOnly,
        nodes: [{ url: config.typesense.endpoint }],
      },
      additionalSearchParameters: {
        query_by: 'displayname,bio,location.text,roles,skills',
        // Pro users first, then most recently active, then highest rated
        sort_by: 'pro:desc,rating:desc,last_active:desc',
      },
    });

    const search = instantsearch({
      indexName: 'users_search',
      searchClient: typesenseInstantsearchAdapter.searchClient,
    });

    search.addWidgets([
      searchBox({
        container: searchContainerRef.current,
        placeholder: 'Search across 1M+ Experts',
        showReset: true,
        showSubmit: false,
        showLoadingIndicator: false,
        cssClasses: {
          root: styles.searchBoxRoot,
          form: styles.searchBoxForm,
          input: styles.searchBoxInput,
        },
      }),

      hits({
        container: hitsContainerRef.current,
        cssClasses: {
          root: styles.hitsRoot,
          list: styles.hitsList,
          item: styles.hitsItem,
          emptyRoot: styles.noResults,
        },
        templates: {
          item: (hit) => `
            <div class="${styles.userCard}">
              <div class="${styles.userCardBody}">
                <div class="${styles.userCardHeader}">
                  <span class="${styles.displayName}">
                    ${hit.displayname || 'No name'}
                  </span>
                </div>
                <div
                  class="${styles.userCardDetails}"
                >
                ${
                  hit.location?.text
                    ? `<p class="${styles.location}"><strong>Location:</strong> ${hit.location.text}</p>`
                    : ''
                }
                ${
                  hit.roles.length > 0
                    ? `<p class="${
                        styles.roles
                      }"><strong>Roles:</strong> ${hit.roles.join(', ')}</p>`
                    : ''
                }
                ${
                  hit.skills.length > 0
                    ? `<p class="${
                        styles.skills
                      }"><strong>Skills:</strong> ${hit.skills.join(', ')}</p>`
                    : ''
                }
                ${
                  hit.sectors.length > 0
                    ? `<p class="${
                        styles.sectors
                      }"><strong>Industries:</strong> ${hit.sectors.join(
                        ', '
                      )}</p>`
                    : ''
                }
                </div>
                <div
                  class="${styles.userCardDetails}"
                >
                ${
                  hit.pro
                    ? `<p class="${styles.pro}"><strong>PRO</strong></p>`
                    : ''
                }
                ${
                  hit.last_active
                    ? `<p class="${
                        styles.lastSeen
                      }"><strong>Last Seen:</strong> ${moment
                        .unix(hit.last_active)
                        .fromNow()}</p>`
                    : ''
                }
                ${
                  hit.rating > 0
                    ? `<p class="${styles.rating}"><strong>Rating:</strong> ${hit.rating} (${hit.total_ratings} reviews)</p>`
                    : ''
                }
                </div>
                ${hit.bio ? `<p class="${styles.bio}">${hit.bio}</p>` : ''}
              </div>
            </div>
          `,
          empty: `<div class="${styles.noResults}">No results found. Try a different search.</div>`,
        },
      }),

      pagination({
        container: paginationContainerRef.current,
        cssClasses: {
          root: styles.paginationRoot,
          list: styles.paginationList,
          item: styles.paginationItem,
          link: styles.paginationLink,
          selectedItem: styles.paginationItemSelected,
          disabledItem: styles.paginationItemDisabled,
        },
      }),
    ]);

    // Track active filter count for the badge
    search.use(() => {
      let mounted = true;
      return {
        subscribe() {},
        unsubscribe() {
          mounted = false;
        },
        onStateChange({ uiState }) {
          if (!mounted) return;
          const indexState = uiState['users_search'] || {};
          const refinements = indexState.refinementList || {};
          const toggle = indexState.toggle || {};
          const count =
            Object.values(refinements).reduce(
              (sum, vals) => sum + vals.length,
              0
            ) + Object.values(toggle).filter(Boolean).length;
          setActiveFilterCount(count);
        },
      };
    });

    search.start();
    setSearchInstance(search);

    return () => {
      search.dispose();
      setSearchInstance(null);
    };
  }, []);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <h1 className={styles.heading}>Browse</h1>

        {/* Loader shown until search instance is ready */}
        {!searchInstance && <Loader loading />}

        {/* All widget containers always mounted so refs exist when widgets initialise.
            Visibility toggled via CSS once the search instance is ready. */}
        <div style={{ display: searchInstance ? 'block' : 'none' }}>
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}>
              <Search size={18} />
            </span>
            <div
              ref={searchContainerRef}
              className={styles.searchBoxContainer}
            />
          </div>

          <FilterBar
            search={searchInstance}
            activeCount={activeFilterCount}
            onOpenFilters={() => setFilterModalOpen(true)}
          />
        </div>

        <div ref={hitsContainerRef} className={styles.resultsContainer} />
        <div
          ref={paginationContainerRef}
          className={styles.paginationContainer}
        />
      </div>

      {/* Filter modal — always rendered, visibility toggled internally */}
      <FilterModal
        search={searchInstance}
        isOpen={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
      />
    </div>
  );
};

export default BrowseTest;
